/* ----------------------------------------------------------------------------
   M0 Onboard wizard — module-scoped constants, seed data + helpers.
   Ports the non-render bits of m0.js (POC roles/rules, score components, AI seed,
   COD-frequency compose, zone meta, sample templates, CSV generators, and the
   in-memory write the Commit step performs on finish()). Frontend-only, no network.
   ---------------------------------------------------------------------------- */
import {
  SERVICE_TYPES,
  TAT_ZONES,
  proshipRow,
  pricingCsv,
  pricingSeed,
  DUMMY_PARTNERS,
  PARTNER_ZONES,
  TODAY,
} from '../dummyData'

/* ───────── POC roles + field/validation helpers ───────── */
export const POC_ROLES = [
  'Key Account Manager (KAM)',
  'Account Head',
  'Business Head / VP',
  'Weight Discrepancy POC',
  'COD Remittance POC',
  'Performance Escalation POC',
  'Technical Integration POC',
  'Finance / Billing POC',
]

export function blankPoc() {
  return {name: '', desig: '', roles: [], email: '', m1: '', m2: '', linkedin: '', notes: ''}
}
export const roleLabel = (r) => r.replace(/ \(.+\)/, '')

const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const RE_PHONE10 = /^[0-9]{10}$/
const RE_GST = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/
const RE_LINKEDIN = /^https?:\/\/([\w-]+\.)?linkedin\.com\//i
const norm = (v) => String(v == null ? '' : v).replace(/[\s-]/g, '')

/* validate one POC; returns {ok, errs:{field:msg}} */
export function validatePoc(poc) {
  const errs = {}
  if (!poc.name || poc.name.trim().length < 2) errs.name = 'Full name is required.'
  if (!RE_EMAIL.test((poc.email || '').trim())) errs.email = 'Enter a valid email.'
  if (!RE_PHONE10.test(norm(poc.m1))) errs.m1 = 'Enter a 10-digit mobile.'
  if (!RE_PHONE10.test(norm(poc.m2))) errs.m2 = 'Enter a 10-digit mobile.'
  else if (norm(poc.m2) === norm(poc.m1) && poc.m2) errs.m2 = 'Mobile 2 must differ from Mobile 1.'
  if (poc.linkedin && !RE_LINKEDIN.test(poc.linkedin)) errs.linkedin = 'Enter a valid linkedin.com URL.'
  if (poc.notes && poc.notes.length > 300) errs.notes = 'Keep notes under 300 characters.'
  /* role coverage is validated at the step level (KAM/Weight/COD/Performance across all POCs),
     matching the mockup — a single POC may carry zero roles. */
  return {ok: Object.keys(errs).length === 0, errs}
}
export const isGst = (v) => RE_GST.test((v || '').trim())
export const isUrl = (v) => !v || /^https:\/\//i.test(v)

/* ───────── draft autosave + resume (localStorage, mirrors mockup ps.m0.onboardDraft) ───────── */
export const DRAFT_KEY = 'ps.m0.onboardDraft'
export function saveDraft(W) {
  try {
    localStorage.setItem(DRAFT_KEY, JSON.stringify({v: 1, savedAt: TODAY.toISOString(), W}))
  } catch (e) {
    /* storage unavailable — silently skip */
  }
}
export function clearDraft() {
  try {
    localStorage.removeItem(DRAFT_KEY)
  } catch (e) {
    /* ignore */
  }
}
export function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY)
    if (!raw) return null
    const d = JSON.parse(raw)
    if (!d || d.v !== 1 || !d.W) return null
    if (!(d.W.step >= 1)) return null
    return d
  } catch (e) {
    return null
  }
}

/* ───────── standard TATs by service type (Zone A–E → local/regional/metro/roi/special)
   Source: "Merchant CP Wise Rates Setup Base Structure" sheet. Pre-fills the TAT step
   as editable defaults; values slower than these are flagged. ───────── */
export const STD_TAT_ZK = ['local', 'regional', 'metro', 'roi', 'special']
export const STD_TAT = {
  FWD_SURFACE_REG: [[1, 2], [2, 3], [3, 5], [5, 7], [7, 9]],
  FWD_SURFACE_LARGE: [[1, 2], [4, 5], [4, 5], [5, 6], [7, 9]],
  FWD_AIR_REG: [[1, 2], [2, 3], [2, 3], [3, 4], [6, 7]],
  FWD_AIR_LARGE: [[1, 2], [2, 3], [2, 3], [3, 4], [6, 7]],
  HV_FWD_REG: [[1, 2], [2, 3], [3, 5], [5, 7], [7, 9]],
  HV_FWD_LARGE: [[1, 2], [4, 5], [4, 5], [5, 6], [7, 9]],
  HV_REVERSE_REG: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  HV_REVERSE_LARGE: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  SDD_FWD_REG: [[0, 1], [0, 1], [0, 1], [1, 2], [1, 2]],
  SDD_FWD_LARGE: [[0, 1], [0, 1], [0, 1], [1, 2], [1, 2]],
  SC_NDD_FWD_REG: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  SC_NDD_FWD_LARGE: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  INTERCITY_NDD_REG: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  INTERCITY_NDD_LARGE: [[1, 2], [1, 2], [1, 2], [1, 2], [1, 2]],
  H2TO4: [[0, 1], [0, 1], [0, 1], [0, 1], [0, 1]],
  SUB2H: [[0, 1], [0, 1], [0, 1], [0, 1], [0, 1]],
  REV_REG_NOQC: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  REV_LARGE_NOQC: [[5, 6], [8, 9], [8, 9], [9, 10], [11, 13]],
  REV_REG_QC: [[5, 6], [6, 7], [8, 9], [9, 10], [11, 12]],
  REV_LARGE_QC: [[5, 6], [6, 7], [8, 9], [9, 10], [11, 12]],
}
export function stdCell(code, zkey) {
  const r = STD_TAT[code] || STD_TAT.FWD_SURFACE_REG
  const p = r[STD_TAT_ZK.indexOf(zkey)]
  return p ? {min: String(p[0]), max: String(p[1])} : {min: '', max: ''}
}

/* ───────── onboarding completeness score (TRD §M0.6) ───────── */
function normFromW(W) {
  return {
    name: W.A.name,
    legal: W.A.legal,
    opType: W.S.mode || '',
    integration: W.A.integration,
    gst: W.A.gst,
    pocs: W.pocs || [],
    verticals: W.A.verticals || [],
    codD2C: W.C.slaD2C,
    codPTL: W.C.slaPTL,
    activeCodes: W.D.status === 'Active' ? 1 : 0,
    agrSigned2: false,
  }
}
export function scoreComponents(n) {
  n = n || {}
  const have = (r) => (n.pocs || []).some((p) => (p.roles || []).some((x) => x.indexOf(r) >= 0))
  const hasD = (n.verticals || []).indexOf('D2C') >= 0
  const hasP = (n.verticals || []).indexOf('PTL') >= 0
  return [
    {key: 'basics', label: 'Parent basics (name, legal, type, integration)', weight: 15, met: !!(n.name && n.legal && n.opType && n.integration)},
    {key: 'gst', label: 'GST number', weight: 5, met: !!n.gst},
    {key: 'integration', label: 'Integration method', weight: 5, met: !!(n.opType && n.integration)},
    {key: 'kam', label: 'Key Account Manager (KAM)', weight: 10, met: have('Key Account')},
    {key: 'accountHead', label: 'Account Head', weight: 5, met: have('Account Head')},
    {key: 'businessHead', label: 'Business Head / VP', weight: 5, met: have('Business Head')},
    {key: 'weight', label: 'Weight Discrepancy POC', weight: 5, met: have('Weight')},
    {key: 'cod', label: 'COD Remittance POC', weight: 5, met: have('COD')},
    {key: 'performance', label: 'Performance Escalation POC', weight: 5, met: have('Performance')},
    {key: 'minPocs', label: 'At least 3 POCs', weight: 5, met: (n.pocs || []).length >= 3},
    {key: 'codD2C', label: 'COD SLA — D2C', weight: 5, met: !hasD || !!n.codD2C},
    {key: 'codPTL', label: 'COD SLA — PTL', weight: 5, met: !hasP || !!n.codPTL},
    {key: 'activeCode', label: 'At least one active code', weight: 15, met: (n.activeCodes || 0) > 0},
    {key: 'agreement', label: 'Signed agreement (Stage 2)', weight: 5, met: !!n.agrSigned2},
  ]
}
export function wizScore(W) {
  let sum = 0
  scoreComponents(normFromW(W)).forEach((c) => {
    if (c.met) sum += c.weight
  })
  return Math.min(100, sum)
}

/* ───────── Direction/Mode → Service Type list ───────── */
export function svcList(direction, mode) {
  const base = SERVICE_TYPES.filter((s) => {
    const isRev = /^REV|REVERSE/.test(s.code) || /Reverse|Returns/.test(s.cat)
    return direction === 'Reverse' ? isRev : direction === 'Forward' ? !isRev : true
  })
  if (direction === 'Reverse' || !mode) return base
  const byMode = base.filter((s) => {
    if (mode === 'Surface') return /SURFACE/.test(s.code) || /Ground/.test(s.cat)
    if (mode === 'Air') return /AIR/.test(s.code) || /Air/.test(s.cat)
    if (mode === 'SDD') return /SDD/.test(s.code) || /Same Day/.test(s.cat)
    if (mode === 'NDD') return /NDD/.test(s.code) || /Next Day/.test(s.cat)
    return true
  })
  return byMode.length ? byMode : base
}

/* ───────── v6 "Profile & Service" (DMS) step model ─────────
   Freight direction (multi) · shipment value (HV/NHV) · payment mode ·
   mode-of-delivery × size grid · grouped canonical service types. */
export const DMS_DIRS = [
  ['FWD', 'Forward', 'Deliver shipments to customers', 'truck', false],
  ['REV_NOQC', 'Reverse · No QC', 'Customer returns without inspection', 'sync', true],
  ['REV_QC', 'Reverse · With QC', 'Returns with door quality check', 'checkcircle', true],
]
export const DMS_VAL = [
  ['HV', 'High Value', 'In-transit insurance & signature POD'],
  ['NHV', 'Non-High Value', 'Standard handling, no mandatory insurance'],
]
export const DMS_PAY = [
  ['PREPAID', 'Prepaid only shipments', 'Customer pays before dispatch'],
  ['COD', 'COD only shipments', 'Cash collected on delivery'],
  ['BOTH', 'Both prepaid and COD shipments', 'Accept prepaid & COD orders'],
]
/* [label, Regular code, Large code] — express rows reuse one code for both */
export const DMS_GRID = [
  ['Surface', 'FWD_SURFACE_REG', 'FWD_SURFACE_LARGE'],
  ['Air', 'FWD_AIR_REG', 'FWD_AIR_LARGE'],
  ['SDD', 'SDD_FWD_REG', 'SDD_FWD_LARGE'],
  ['Same-City NDD', 'SC_NDD_FWD_REG', 'SC_NDD_FWD_LARGE'],
  ['Inter-City NDD', 'INTERCITY_NDD_REG', 'INTERCITY_NDD_LARGE'],
  ['2–4 hour delivery', 'H2TO4', 'H2TO4'],
  ['Sub-2 hour', 'SUB2H', 'SUB2H'],
]
export const DMS_GROUPS = [
  ['FORWARD', [['FWD_SURFACE_REG', 'Surface · Regular'], ['FWD_SURFACE_LARGE', 'Surface · Large'], ['FWD_AIR_REG', 'Air · Regular'], ['FWD_AIR_LARGE', 'Air · Large']]],
  ['HIGH VALUE', [['HV_FWD_REG', 'Forward · Regular'], ['HV_FWD_LARGE', 'Forward · Large'], ['HV_REVERSE_REG', 'Reverse · Regular'], ['HV_REVERSE_LARGE', 'Reverse · Large']]],
  ['SDD', [['SDD_FWD_REG', 'Forward · Regular'], ['SDD_FWD_LARGE', 'Forward · Large']]],
  ['NDD', [['SC_NDD_FWD_REG', 'Same-City · Regular'], ['SC_NDD_FWD_LARGE', 'Same-City · Large'], ['INTERCITY_NDD_REG', 'Inter-City · Regular'], ['INTERCITY_NDD_LARGE', 'Inter-City · Large']]],
  ['EXPRESS', [['H2TO4', '2–4 hour delivery'], ['SUB2H', 'Sub-2 hour delivery']]],
  ['REVERSE', [['REV_REG_NOQC', 'Regular · No QC'], ['REV_LARGE_NOQC', 'Large · No QC'], ['REV_REG_QC', 'Regular · With QC'], ['REV_LARGE_QC', 'Large · With QC']]],
]
export const DMS_REVMAP = {REV_NOQC: ['REV_REG_NOQC', 'REV_LARGE_NOQC'], REV_QC: ['REV_REG_QC', 'REV_LARGE_QC']}

/* ───────── COD remittance frequency compose (B1) ───────── */
export function composeFreq(codDPlus, codTimesPerWeek) {
  const d = String(codDPlus || '').trim()
  const w = String(codTimesPerWeek || '').trim()
  const parts = []
  if (d !== '') parts.push('D+' + d)
  if (w !== '') parts.push(w + ' times a week')
  return parts.join(', ')
}

/* ───────── AI extraction seed (preview matrix + T&Cs) ───────── */
export const AI_ZONES = [['1', 'Local'], ['2', 'Regional'], ['3', 'Metro'], ['4', 'ROI'], ['5', 'Special']]
export const AI_SLABS = ['0-500', '500-1000', '1000-1500', '1500-2000', '2000-2500', '2500-3000', '3000-3500', '3500-4000']
export function aiSeed() {
  const base = [23, 40, 57, 74, 91, 108, 125, 142]
  const delta = [0, 9, 16, 24, 38]
  const rates = {}
  AI_ZONES.forEach((z, zi) => {
    rates[z[0]] = {}
    AI_SLABS.forEach((s, si) => {
      rates[z[0]][s] = (base[si] + delta[zi]).toFixed(2)
    })
  })
  return rates
}
export function aiTcSeed() {
  return {
    cod_fixed: '30',
    cod_pct: '1.5',
    cod_op: 'MAX',
    rto_pct: '100',
    fuel_pct: '12',
    docket: '',
    rvp_formula: 'Higher of (flat, % of FWD)',
    rvp_flat: '55',
    rvp_pct: '150',
    qc: '65',
    remit: 'D+7 · 4 times / month',
  }
}
export function aiCodDef(T) {
  return 'Charge = ' + (T.cod_op || 'MAX') + '(₹' + (T.cod_fixed || '0') + ' fixed, ' + (T.cod_pct || '0') + '% of COD value)'
}

/* ───────── zone meta (override builder + test resolver) ───────── */
export const ZN_META = [
  [1, 'LOCAL', '#2E7D32'],
  [2, 'REGIONAL', '#0288D1'],
  [3, 'METRO', '#1976D2'],
  [4, 'ROI', '#6A1B9A'],
  [5, 'SPECIAL', '#B7791F'],
]

/* ───────── sample templates (exact copies served by in-flow buttons) ───────── */
export const SVC_TPL =
  'pincode,city,state,serviceable\n400001,Mumbai,Maharashtra,Yes\n560001,Bengaluru,Karnataka,Yes\n110001,New Delhi,Delhi,Yes\n781001,Guwahati,Assam,No\n744101,Port Blair,Andaman & Nicobar,No\n'
const ZONE_ROWS =
  'pickup_pincode,zone\n110042,1\n110001,1\n122001,2\n122002,2\n201301,2\n400001,3\n560001,3\n600001,3\n302001,4\n452001,4\n781001,5\n744101,5\n'
export const ZONE_TPLS = [
  {key: 'xpressbees', label: 'Xpressbees (XB) — standard', name: 'xpressbees-zone-template.csv', content: ZONE_ROWS},
  {key: 'delhivery', label: 'Delhivery', name: 'delhivery-zone-template.csv', content: ZONE_ROWS},
  {key: 'bluedart', label: 'Blue Dart', name: 'bluedart-zone-template.csv', content: ZONE_ROWS},
]
export const RATE_TPL = pricingCsv(pricingSeed())

/* ───────── rate + TAT CSV generators (Review & Commit) ───────── */
function pricingFromAi(W) {
  const R = W.AI.rates || aiSeed()
  const T = W.AI.tc || aiTcSeed()
  const rows = []
  AI_ZONES.forEach((z) => {
    AI_SLABS.forEach((s) => {
      const p = s.split('-')
      rows.push(
        proshipRow(+z[0], +p[0], +p[1], +R[z[0]][s], null, {
          fsc: +T.fuel_pct || 0,
          codPct: +T.cod_pct || 0,
          codOp: T.cod_op || 'MAX',
          codFixed: +T.cod_fixed || 0,
          rto: +T.rto_pct || 90,
          rvpNoQc: +T.rvp_flat || 0,
          rvpQc: +T.rvp_flat || 0,
        })
      )
    })
  })
  return rows
}
export function genRateCsv(W) {
  return pricingCsv(W.COMM.method === 'ai' ? pricingFromAi(W) : W.COMM.matrix && W.COMM.matrix.length ? W.COMM.matrix : pricingSeed())
}
export function genTatCsv(W) {
  const today = TODAY.toISOString().slice(0, 10)
  const lines = ['courier_code,service_type,zone,min_days,max_days,effective_from']
  ;(W.D.serviceTypes || []).forEach((stc) => {
    const t = W.TAT[stc] || {}
    TAT_ZONES.forEach((z) => {
      const v = t[z.key] || {}
      if (v.min !== '' && v.min != null) lines.push([W.D.code, stc, z.name, v.min, v.max, today].join(','))
    })
  })
  return lines.join('\n')
}

/* ───────── backend-format TAT CSV — header `zone,Min Tat days,Max Tat Days`,
   one row per zone 1..5 (Local/Regional/Metro/ROI/Special), values from the
   PRIMARY service type only. Uploaded via tools/upload/servicability (TAT). */
export function genZoneTatCsv(W) {
  const primary = W.D.serviceTypePrimary || (W.D.serviceTypes || [])[0]
  const t = (primary && W.TAT[primary]) || {}
  const lines = ['zone,Min Tat days,Max Tat Days']
  TAT_ZONES.forEach((z) => {
    const v = t[z.key] || {}
    lines.push([z.n, v.min || '', v.max || ''].join(','))
  })
  return lines.join('\n')
}

/* ───────── backend-mandatory courier attribute defaults (Account Code step) ───────── */
/* All courier attributes start EMPTY by design: these are facts from the courier's
   rate card / MSA and ops must enter them consciously — no silent business defaults.
   Only onboardedDate (factual: today) and integration (derived from the parent's
   integration method, editable) get prefilled at the step level. */
export function defaultAttrs() {
  return {
    minChargeableWeight: '',
    maxWeightSinglePiece: '',
    maxWeightPerShipment: '',
    volumetricDivisor: '',
    maxLength: '',
    maxWidth: '',
    maxHeight: '',
    maxGirth: '',
    maxLwhSum: '',
    chargeableWeightRule: '',
    multiPieceAllowed: '',
    maxPiecesPerShipment: '',
    fragileHandlingAvailable: '',
    specialPackagingRequired: '',
    remarks: '',
    metroServiceable: '',
    nddEligible: '',
    sddEligible: '',
    forwardSlaMetroDays: '',
    forwardSlaNonMetroDays: '',
    pickupCutoffTime: '',
    pickupDays: '',
    maxDeliveryAttempts: '',
    deliveryDays: '',
    realTimeTracking: '',
    trackingType: '',
    onboardedDate: TODAY.toISOString().slice(0, 10),
    integration: '',
    supportedShipmentType: 'B2C',
  }
}

/* parent-zone lookup for the Courier Zones step "Replicate for parent" card */
export function partnerZoneFor(parentNameOrId) {
  const p = DUMMY_PARTNERS.find((x) => x.name === parentNameOrId || x.id === parentNameOrId)
  return p ? {partner: p, zones: PARTNER_ZONES[p.id] || null} : {partner: null, zones: null}
}
